import React, { useState, useEffect } from "react";
import { X, Plus, Trash2, AlertTriangle } from "lucide-react";
import sectionService from "../../../services/sectionService";

// canAdd, canEdit, canDelete passed from TableManagement based on RBAC.
// Defaults to true so any existing usage without these props is unaffected.
export default function SectionManager({ branchId, onSave, onClose, canAdd = true, canEdit = true, canDelete = true }) {
  const [sectionList, setSectionList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState(null);

  const loadSections = async () => {
    try {
      const res = await sectionService.getAll();
      let sections = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
        ? res.data
        : [];
      if (branchId) {
        sections = sections.filter(s => s.branchId === branchId);
      }
      setSectionList(sections);
    } catch (error) {
      console.error("Error fetching sections:", error);
      setMessage("Failed to fetch sections.");
    }
  };

  useEffect(() => {
    loadSections();
  }, []);

  const handleAddSection = () => {
    const newSection = {
      id: Date.now(),
      name: "",
      description: "",
      isActive: true,
      isNew: true,
    };
    setSectionList([...sectionList, newSection]);
  };

  const handleUpdateSection = (id, field, value) => {
    setSectionList((prev) =>
      prev.map((s) =>
        s.id === id
          ? { ...s, [field]: value, isEdited: !s.isNew ? true : s.isEdited }
          : s
      )
    );
  };

  const confirmDelete = (id) => {
    setSectionToDelete(id);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!sectionToDelete) return;
    try {
      await sectionService.delete(sectionToDelete);
      setMessage("Section deleted successfully!");
      await loadSections();
      onSave?.(null, "Section deleted successfully!");
    } catch (error) {
      console.error("Error deleting section:", error);
      setMessage("Failed to delete section.");
    } finally {
      setShowDeleteDialog(false);
      setSectionToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setSectionToDelete(null);
  };

  const toggleActive = (id) => {
    setSectionList((prev) =>
      prev.map((s) =>
        s.id === id
          ? { ...s, isActive: !s.isActive, isEdited: !s.isNew ? true : s.isEdited }
          : s
      )
    );
  };

  const handleSave = async () => {
    const validSections = sectionList.filter((s) => s.name.trim() !== "");
    if (validSections.length === 0) {
      setMessage("Please add at least one valid section.");
      return;
    }
    setLoading(true);
    setMessage("");
    try {
      if (canAdd) {
        const newSections = validSections.filter((s) => s.isNew);
        for (const s of newSections) {
          await sectionService.create({
            name: s.name,
            description: s.description,
            isActive: s.isActive,
          });
        }
      }
      if (canEdit) {
        const updatedSections = validSections.filter((s) => !s.isNew && s.isEdited);
        for (const s of updatedSections) {
          await sectionService.update(s.id, {
            name: s.name,
            description: s.description,
            isActive: s.isActive,
          });
        }
      }
      onSave?.();
      onClose();
    } catch (error) {
      console.error("Error saving sections:", error);
      setMessage("Failed to save sections.");
    } finally {
      setLoading(false);
    }
  };

  // Whether Save should be shown — needs at least add or edit permission
  const canSave = canAdd || canEdit;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden relative">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Manage Sections</h2>
            <p className="text-xs text-gray-600 mt-0.5">Organize tables by sections or areas</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-md transition-colors">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Section List */}
        <div className="overflow-y-auto max-h-[calc(90vh-180px)] p-6">
          <div className="space-y-3">
            {sectionList.map((section, index) => {
              // A row is editable if: it's a new row (needs canAdd) or existing row (needs canEdit)
              const isEditable = section.isNew ? canAdd : canEdit;
              return (
                <div
                  key={section.id || index}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="flex items-center justify-center w-8 h-8 bg-teal-100 text-teal-700 rounded font-semibold text-sm">
                    {index + 1}
                  </div>

                  {isEditable ? (
                    <input
                      type="text"
                      value={section.name}
                      onChange={(e) => handleUpdateSection(section.id, "name", e.target.value)}
                      placeholder="Section name (e.g., Main Hall)"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                    />
                  ) : (
                    <span className="flex-1 px-3 py-2 text-sm text-gray-700">{section.name}</span>
                  )}

                  {isEditable ? (
                    <input
                      type="text"
                      value={section.description}
                      onChange={(e) => handleUpdateSection(section.id, "description", e.target.value)}
                      placeholder="Description (optional)"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                    />
                  ) : (
                    <span className="flex-1 px-3 py-2 text-sm text-gray-500">{section.description || '—'}</span>
                  )}

                  {isEditable ? (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={section.isActive}
                        onChange={() => toggleActive(section.id)}
                        className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                      />
                      <span className="text-xs text-gray-600">Active</span>
                    </label>
                  ) : (
                    <span className={`text-xs font-medium px-2 py-1 rounded ${section.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {section.isActive ? 'Active' : 'Inactive'}
                    </span>
                  )}

                  {/* Delete — only for existing (non-new) sections, requires canDelete */}
                  {!section.isNew && canDelete && (
                    <button
                      onClick={() => confirmDelete(section.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              );
            })}

            {/* Add Section row — requires canAdd */}
            {canAdd && (
              <button
                onClick={handleAddSection}
                className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-teal-500 hover:text-teal-600 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
              >
                <Plus size={18} />
                <span>Add Section</span>
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-3 bg-gray-50 flex items-center justify-between">
          <p className="text-xs text-gray-600">
            {sectionList.length} section{sectionList.length !== 1 ? "s" : ""}
          </p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition-colors text-sm"
            >
              {canSave ? 'Cancel' : 'Close'}
            </button>
            {canSave && (
              <button
                onClick={handleSave}
                disabled={loading}
                className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors text-sm"
              >
                {loading ? "Saving..." : "Save Sections"}
              </button>
            )}
          </div>
        </div>

        {message && (
          <div className="px-6 py-2 text-sm text-gray-700 text-center">{message}</div>
        )}

        {showDeleteDialog && (
          <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
            <div className="bg-white rounded-lg p-6 shadow-lg w-80 text-center">
              <AlertTriangle size={40} className="text-red-500 mx-auto mb-2" />
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Delete Section?</h3>
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to delete this section? This will permanently remove it along with all associated tables.
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={handleCancelDelete}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}